import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { listDpRequestsOfCollaborator } from '../../services/dpRequestQueries';
import { flattenDpDocuments } from '../../utils/dpDocuments';
import { DP_ROLE_LIST } from '../../dpRequests.definitions';
import type { CollaboratorDpDocumentsProps } from './collaboratorDpDocuments.types';

export function CollaboratorDpDocuments({ collaboratorId }: CollaboratorDpDocumentsProps) {
    const { userHasRole } = useAuth();
    const isDp = DP_ROLE_LIST.some(role => userHasRole(role));

    const query = useQuery({
        queryKey: ['dp-requests', 'documents', collaboratorId],
        queryFn: () => listDpRequestsOfCollaborator(collaboratorId),
        enabled: isDp && Boolean(collaboratorId),
    });

    if (!isDp) return null;

    const rows = flattenDpDocuments(query.data ?? []);

    return (
        <section className="space-y-3" aria-labelledby="dp-documents">
            <h3 id="dp-documents" className="font-montserrat text-base font-semibold text-raro-gray-900">Documentos enviados ao DP</h3>
            {query.isPending && <div className="h-16 animate-pulse rounded-xl bg-raro-gray-100" />}
            {query.isError && <p className="text-sm text-rose-700">Não foi possível carregar os documentos.</p>}
            {query.data && rows.length === 0 && (
                <p className="text-sm text-raro-gray-500">Nenhum documento enviado ao DP.</p>
            )}
            {rows.length > 0 && (
                <ul className="divide-y divide-raro-gray-100 rounded-xl border border-raro-gray-200 bg-white">
                    {rows.map(row => (
                        <li key={row.key} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                            <span className="min-w-0 truncate">
                                <span className="font-medium text-raro-gray-900">{row.attachment.name}</span>
                                <span className="text-raro-gray-500"> · {row.typeLabel}</span>
                            </span>
                            <a href={row.attachment.url} target="_blank" rel="noopener noreferrer" className="shrink-0 font-semibold text-primary-700 hover:underline">
                                Abrir
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

export default CollaboratorDpDocuments;
